import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useSpeechToText, { isSpeechRecognitionSupported } from './useSpeechToText';
import MicToggleButton from './MicToggleButton';
import './review.css';

/**
 * Sticky review footer — ported from the mock's `.revfoot` (docs/design/specimen/
 * review.html, #450): a single-line overall-feedback `.input` (same mic toggle as
 * CommentComposer, reused via the shared useSpeechToText hook) + a primary "Complete
 * review" `.btn`, which POSTs a `kind:'overall'` comment on the ROOT item (spec
 * §Comments — "stored as a normal item_comments row on the container item"). An
 * empty field is a valid "no overall comment" completion (mirrors the mockup's
 * `finish` handler) — `onComplete` decides whether to actually POST.
 *
 * #450's ONE intentional deviation from the mock: `placement="top"` renders this
 * ABOVE the item list (BreakdownTab.js) instead of the mock's bottom-of-page
 * position — the mock itself is otherwise followed structurally (`.revfoot`,
 * `.input`, `.micbtn`, `.btn.primary`).
 */
function ReviewFooter({ rootTitle = '', onComplete, placement = 'bottom' }) {
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef(null);

  const insertAtCaret = useCallback((text) => {
    const el = textareaRef.current;
    setValue((prev) => {
      const start = el ? el.selectionStart : prev.length;
      const end = el ? el.selectionEnd : prev.length;
      const before = prev.slice(0, start);
      const after = prev.slice(end);
      const sep = before && !/\s$/.test(before) ? ' ' : '';
      return `${before}${sep}${text}${after}`;
    });
  }, []);

  const stt = useSpeechToText({ onTranscript: insertAtCaret });

  const handleComplete = useCallback(async () => {
    stt.stop();
    setSubmitting(true);
    try {
      await onComplete(value.trim());
      setValue('');
    } finally {
      setSubmitting(false);
    }
  }, [onComplete, value, stt]);

  // Esc always force-stops dictation (Security §8) — there's no "cancel" for
  // this always-visible field, just mic release.
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') stt.stop();
  };

  return (
    // #793 H: `role="presentation"` — a layout row whose children (input, mic toggle,
    // submit) carry all the semantics. The keydown is DELEGATION: Escape must force-stop
    // dictation whichever of those children holds focus, so it cannot live on any one of
    // them. Like CommentComposer, this handler adds keyboard behaviour rather than
    // withholding it, which is the opposite of what the rule guards against.
    <div
      className={`revfoot${placement === 'top' ? ' revfoot-top' : ''}`}
      role="presentation"
      data-testid="review-footer"
      onKeyDown={handleKeyDown}
    >
      <input
        ref={textareaRef}
        className="input rmdv-input-grow"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={`Overall feedback on this review… (stored as an 'overall' comment on ${rootTitle || 'this item'})`}
        aria-label="Overall review feedback"
      />
      {isSpeechRecognitionSupported() && (
        <MicToggleButton listening={stt.listening} onToggle={stt.toggle} />
      )}
      {stt.listening && <span className="t-label" style={{ color: 'var(--error)' }}>Listening…</span>}
      <button
        type="button"
        className="btn primary"
        onClick={handleComplete}
        disabled={submitting}
        data-testid="complete-review-btn"
      >
        Complete review
      </button>
    </div>
  );
}

ReviewFooter.propTypes = {
  rootTitle: PropTypes.string,
  onComplete: PropTypes.func.isRequired,
  placement: PropTypes.oneOf(['top', 'bottom']),
};

export default ReviewFooter;
